/*
  Business Forecast - how many guests a day is likely to have, worked out from the venue's own bookings.

  Days are 'YYYY-MM-DD' keys, read straight off the stored values as the rest of the app reads them
  (wall clock with a Z), and turned into whole day numbers for the arithmetic.

  Two estimates are blended:
  - Booking pace. Every booking has a created date, so for any past day we can rebuild what was on
    the books a given number of days out, and what it finished on. The usual share booked by then on
    that weekday turns what is booked now into an estimate. Walk-ins are entered as bookings, so they
    show up as late bookings and are counted in the share.
  - Baseline. The average finish of recent days on the same weekday, using holiday days for a day in
    a holiday period (a timeset other than the default, or a bank holiday) where there are enough of
    them - averaged with the same week last year, scaled by this year's trend, once there is a year.
  The pace estimate is weighted by the share usually booked by then - so close to the day, with most
  guests usually booked, it leads, and further out the baseline does.

  Then the weather: how wet days and warm days have gone here against what was expected for them is
  applied to the guests not booked yet.

  Everything is worked out "as at the start of" a day, from days that had already finished, so the
  same code rebuilds what the forecast would have said in the past (the backtest).
*/

export interface ForecastBooking {
  day: number;          //visit day
  created: number;      //day the booking was made
  guests: number;
}

export interface DayWeather {
  rain: number;         //mm over the day
  tmax: number;         //°C, the day's high
}

export interface ForecastInput {
  bookings: ForecastBooking[];
  holidays: Map<number, string[]>;    //day -> the names of the holiday timesets and bank holidays on it
  firstDay: number;                   //first day loaded - days before it are unknown, not empty
  weather?: Map<number, DayWeather>;  //actual weather for past days, forecast for the coming ones
}

export interface Forecast {
  day: number;
  lead: number;                 //days between the snapshot and the day
  bookedAtSnapshot: number;     //guests booked before the snapshot day began
  pace: number | null;          //estimate from booking pace
  share: number | null;         //usual share of guests booked by this point
  baseline: number | null;      //estimate from similar days, and last year where there is one
  baselineDays: number;         //how many recent days the baseline came from
  lastYear: number | null;      //estimate from the same week last year
  weather: DayWeather | null;
  weatherFactor: number | null; //what the weather did to the guests not yet booked
  estimate: number | null;
  holidayNames: string[];
  reasons: string[];
}

const DAY_MS = 86400000;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

//How many same-weekday days the pace share and the baseline are taken from
const SIMILAR_DAYS = 8;
//Fewest days a share or a baseline is worked out from
const MIN_DAYS = 3;

export function dayNumber(value: string | Date | null | undefined): number | null {
  if (value == null)
    return null;

  const text = typeof value == 'string' ? value : value.toISOString();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);

  return match == null ? null : Math.floor(Date.UTC(+match[1], +match[2] - 1, +match[3]) / DAY_MS);
}

export function dayDate(day: number): Date {
  return new Date(day * DAY_MS);
}

//Today on this device's calendar, as a day number
export function today(): number {
  const now = new Date();
  return Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS);
}

export function weekday(day: number): number {
  return dayDate(day).getUTCDay();
}

export function weekdayName(day: number): string {
  return WEEKDAYS[weekday(day)];
}

/*
  The bookings grouped by visit day, each day's list sorted by created day, so "booked before a day"
  is a walk along the list.
*/
export class BookingHistory {
  private byDay = new Map<number, ForecastBooking[]>();
  private finals = new Map<number, number>();

  constructor(public input: ForecastInput) {
    input.bookings.forEach(b => {
      if (!this.byDay.has(b.day))
        this.byDay.set(b.day, []);
      this.byDay.get(b.day)!.push(b);
      this.finals.set(b.day, (this.finals.get(b.day) ?? 0) + b.guests);
    });

    this.byDay.forEach(list => list.sort((a, b) => a.created - b.created));
  }

  //Guests the day finished on
  final(day: number): number {
    return this.finals.get(day) ?? 0;
  }

  //Guests booked for the day before the snapshot day began
  bookedBefore(day: number, snapshot: number): number {
    let total = 0;
    for (const b of this.byDay.get(day) ?? []) {
      if (b.created >= snapshot)
        break;
      total += b.guests;
    }
    return total;
  }

  weather(day: number): DayWeather | null {
    return this.input.weather?.get(day) ?? null;
  }

  /*
    How a finished day did against what was expected for it from the days before it: 1.2 is 20%
    busier than the similar days average. The weather effect is learned from these. It does not
    depend on the snapshot, so each day's is worked out once.
  */
  private surprises = new Map<number, number | null>();

  surprise(day: number): number | null {
    if (!this.surprises.has(day)) {
      const similar = similarDays(this, day, day, this.learnableDays(day));
      this.surprises.set(day, similar == null || similar.value <= 0 ? null : this.final(day) / similar.value);
    }
    return this.surprises.get(day)!;
  }

  holidayNames(day: number): string[] {
    return this.input.holidays.get(day) ?? [];
  }

  isHoliday(day: number): boolean {
    return this.holidayNames(day).length > 0;
  }

  /*
    Finished days the forecast can learn from at the snapshot: loaded, before the snapshot day, and
    with guests - a day with none is taken to be closed, which would drag both estimates down.
    Most recent first.
  */
  learnableDays(snapshot: number): number[] {
    const days: number[] = [];
    for (let d = snapshot - 1; d >= this.input.firstDay; d--) {
      if (this.final(d) > 0)
        days.push(d);
    }
    return days;
  }
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function mean(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function leadText(lead: number): string {
  if (lead == 0)
    return 'by the start of the day';
  if (lead == 1)
    return 'by the day before';
  return 'by ' + lead + ' days before the day';
}

function guests(n: number): string {
  return Math.round(n) + (Math.round(n) == 1 ? ' guest' : ' guests');
}

//"(roughly 1 in 6)" for a share, which reads more easily than a percentage - left out from 40% up
function oneIn(share: number): string {
  return share > 0 && share < 0.4 ? ' (roughly 1 in ' + Math.round(1 / share) + ')' : '';
}

//"17% more" / "17% fewer"
function moreOrFewer(factor: number): string {
  const change = Math.round((factor - 1) * 100);
  return change >= 0 ? change + '% more' : -change + '% fewer';
}

//The forecast for a day, as it would have been at the start of the snapshot day
export function forecastDay(history: BookingHistory, day: number, snapshot: number): Forecast {
  const lead = day - snapshot;
  const learnable = history.learnableDays(snapshot);
  const sameWeekday = learnable.filter(d => weekday(d) == weekday(day));
  const holidayNames = history.holidayNames(day);
  const holiday = holidayNames.length > 0;
  const reasons: string[] = [];

  const booked = history.bookedBefore(day, snapshot);

  /*
    The reasons are written for venue staff, as the steps of the sum in order: what is booked, what
    that points to, what similar days point to, how the two are weighed up, then the weather.
  */
  if (holiday)
    reasons.push('This day falls in a holiday period (' + holidayNames.join(', ') + ').');

  reasons.push('Before today, ' + guests(booked) + ' had booked.');

  //Pace: the usual share of the final booked this far out, on the same weekday where there are
  //enough of them, otherwise on any recent day
  let shareDays = sameWeekday.slice(0, SIMILAR_DAYS);
  let shareOn = 'a typical ' + weekdayName(day);
  let thisDay = 'this ' + weekdayName(day);
  if (shareDays.length < MIN_DAYS) {
    shareDays = learnable.slice(0, SIMILAR_DAYS * 2);
    shareOn = 'a typical recent day';
    thisDay = 'this day';
  }

  let share: number | null = null;
  let pace: number | null = null;

  if (shareDays.length >= MIN_DAYS) {
    share = median(shareDays.map(d => history.bookedBefore(d, d - lead) / history.final(d)));

    //Too little is usually booked this far out for what is booked now to say much
    if (share >= 0.05)
      pace = booked / share;

    let text = lead == 0
      ? 'On ' + shareOn + ', about ' + Math.round(share * 100) + '% of the guests have booked before the day itself - the rest book on the day or walk in.'
      : 'On ' + shareOn + ', ' + (share < 0.5 ? 'only ' : '') + 'about ' + Math.round(share * 100) + '%' + oneIn(share) + ' of the guests have booked ' + leadText(lead) + '.';

    text += pace != null
      ? ' If ' + thisDay + ' follows the same pattern, the ' + booked + ' booked before today point to about ' + Math.round(pace) + ' on the day.'
      : ' That is too few to go on, so bookings so far are not used.';

    reasons.push(text);
  }

  //Baseline: recent similar days, blended with the same week last year where there is one
  const similar = similarDays(history, day, snapshot, learnable);
  const lastYear = lastYearAt(history, day, snapshot);
  let baseline: number | null = similar?.value ?? null;

  if (similar != null)
    reasons.push(similar.text);

  //Weighted by how many days each came from, so a few days last year do not outweigh eight recent ones
  if (lastYear != null) {
    reasons.push(lastYear.text);
    if (baseline != null) {
      baseline = (baseline * similar!.days + lastYear.value * lastYear.days) / (similar!.days + lastYear.days);
      reasons.push('Putting recent days and last year together, similar days point to about ' + Math.round(baseline) + '.');
    }
    else
      baseline = lastYear.value;
  }

  //Blend, leaning on pace as more of the day is usually booked
  let estimate: number | null = null;
  if (pace != null && baseline != null) {
    estimate = share! * pace + (1 - share!) * baseline;

    const pacePart = Math.round(share! * 100);
    reasons.push((share! < 0.25
      ? 'This far ahead, most guests have not booked yet, so bookings so far could easily be misleading. '
      : share! < 0.5
      ? 'Fewer than half the guests have usually booked by now, so bookings so far are only a partial guide. '
      : 'By now, most guests have usually booked, so bookings so far are a good guide. ') +
      'They count for ' + pacePart + '% of the forecast and similar days for ' + (100 - pacePart) + '%: ' +
      pacePart + '% of ' + Math.round(pace) + ' plus ' + (100 - pacePart) + '% of ' + Math.round(baseline) + ' is about ' + Math.round(estimate) + '.' +
      (share! < 0.5 ? ' As more guests book, bookings so far will count for more.' : ''));
  }
  else if (pace != null) {
    estimate = pace;
    reasons.push('There are not enough similar days yet to compare with, so the forecast goes on bookings so far: about ' + Math.round(pace) + '.');
  }
  else if (baseline != null) {
    estimate = baseline;
    reasons.push('So the forecast goes on similar days: about ' + Math.round(baseline) + '.');
  }

  /*
    Weather moves the guests not booked yet - those already booked are coming whatever the weather.
    So the day's usual weather effect is applied to the gap between the estimate and what is booked.
  */
  const weather = history.weather(day);
  let weatherFactor: number | null = null;

  if (estimate != null && weather != null) {
    const effect = weatherEffect(history, weather, snapshot, learnable);
    if (effect != null) {
      weatherFactor = effect.factor;
      estimate = booked + (estimate - booked) * effect.factor;
      reasons.push(...effect.reasons);
      if (effect.factor != 1)
        reasons.push('Allowing for the weather, about ' + Math.round(Math.max(estimate, booked)) + '.');
    }
  }

  if (estimate != null && estimate < booked) {
    estimate = booked;
    reasons.push('That is fewer than have already booked, so the forecast is the ' + booked + ' already booked.');
  }

  if (estimate == null)
    reasons.push('There is not enough booking history yet to make a forecast, so this only shows what is booked so far.');

  return { day, lead, bookedAtSnapshot: booked, pace, share, baseline, baselineDays: similar?.days ?? 0, lastYear: lastYear?.value ?? null, weather, weatherFactor, estimate, holidayNames, reasons };
}

/*
  The average of recent days like this one, from days finished before the snapshot: the same
  weekday, holiday or not to match. Where there are too few holiday days on the weekday, ordinary
  days are scaled by how holidays usually compare.
*/
function similarDays(history: BookingHistory, day: number, snapshot: number, learnable: number[]): { value: number, days: number, text: string } | null {
  const sameWeekday = learnable.filter(d => weekday(d) == weekday(day));
  const holiday = history.isHoliday(day);
  const like = sameWeekday.filter(d => history.isHoliday(d) == holiday).slice(0, SIMILAR_DAYS);

  if (like.length >= MIN_DAYS || (holiday && like.length >= 2)) {
    const value = mean(like.map(d => history.final(d)));
    return { value, days: like.length, text: 'Recent ' + (holiday ? 'holiday ' : '') + weekdayName(day) + 's have had about ' + guests(value) + ' (the average of the last ' + like.length + ').' };
  }

  if (!holiday)
    return null;

  const ordinary = sameWeekday.filter(d => !history.isHoliday(d)).slice(0, SIMILAR_DAYS);
  if (ordinary.length < MIN_DAYS)
    return null;

  const ordinaryMean = mean(ordinary.map(d => history.final(d)));
  const factor = holidayFactor(history, learnable);

  const ordinaryText = 'Recent ordinary ' + weekdayName(day) + 's have had about ' + guests(ordinaryMean) + ' (the average of the last ' + ordinary.length + ').';

  if (factor == null)
    return { value: ordinaryMean, days: ordinary.length, text: ordinaryText + ' There are no past holidays here to compare with yet, so nothing extra is added for the holiday.' };

  return {
    value: ordinaryMean * factor,
    days: ordinary.length,
    text: ordinaryText + ' Holiday days here have usually had ' + moreOrFewer(factor) + ' guests than ordinary days, so that points to about ' + Math.round(ordinaryMean * factor) + '.'
  };
}

/*
  The same week last year - the same weekday 52 weeks back and a week either side, holiday or not to
  match, as holidays move from year to year - scaled by how the last 8 weeks compare with the same
  8 weeks a year earlier. Needs a year and two months of history, so most venues will not have it yet.
*/
function lastYearAt(history: BookingHistory, day: number, snapshot: number): { value: number, days: number, text: string } | null {
  const known = (d: number) => d >= history.input.firstDay && d < snapshot && history.final(d) > 0;

  const then = [day - 371, day - 364, day - 357].filter(d => known(d) && history.isHoliday(d) == history.isHoliday(day));
  if (then.length < 2)
    return null;

  //Day against the same day a year before, only where both are known and both holiday or both not,
  //so missing days or a moved holiday do not pass for a trend
  let recent = 0, before = 0, pairs = 0;
  for (let d = snapshot - 56; d < snapshot; d++) {
    if (known(d) && known(d - 364) && history.isHoliday(d) == history.isHoliday(d - 364)) {
      recent += history.final(d);
      before += history.final(d - 364);
      pairs++;
    }
  }
  if (pairs < 20)
    return null;

  const trend = Math.min(2, Math.max(0.5, recent / before));
  const lastYearMean = mean(then.map(d => history.final(d)));
  const value = lastYearMean * trend;

  return {
    value,
    days: then.length,
    text: 'Around the same week last year, a day like this had about ' + guests(lastYearMean) + '. The last 8 weeks have had ' +
      moreOrFewer(trend) + ' guests than the same weeks a year ago, so last year points to about ' + Math.round(value) + ' this year.'
  };
}

/*
  How wet and warm days have gone against what was expected for them (the recent similar days
  average, worked out as at each day), for the venue's own past days. A rain effect needs some wet
  days and some dry ones to learn from, and the same for warm days, and each is kept within
  reason so a handful of odd days cannot swing a forecast wildly.
*/
const WET_MM = 3;
const WARM_C = 20;
const MIN_WEATHER_DAYS = 5;

function weatherEffect(history: BookingHistory, weather: DayWeather, snapshot: number, learnable: number[]): { factor: number, reasons: string[] } | null {
  const past = learnable.slice(0, 365)
    .map(d => ({ w: history.weather(d), r: history.surprise(d) }))
    .filter(p => p.w != null && p.r != null) as { w: DayWeather, r: number }[];

  let factor = 1;
  let any = false;
  const reasons: string[] = [];

  const learn = (isIt: (w: DayWeather) => boolean, name: string, describe: string) => {
    const inGroup = past.filter(p => isIt(p.w)).map(p => p.r);
    const outGroup = past.filter(p => !isIt(p.w)).map(p => p.r);
    if (inGroup.length < MIN_WEATHER_DAYS || outGroup.length < MIN_WEATHER_DAYS)
      return;

    any = true;
    const effect = Math.min(1.5, Math.max(0.67, median(inGroup) / median(outGroup)));

    if (isIt(weather)) {
      factor *= effect;
      reasons.push(describe + ' On ' + name + ' days, this venue has usually had ' + moreOrFewer(effect) + ' guests than expected (from ' + inGroup.length +
        ' past ' + name + ' days), so the guests still to book are ' + (effect >= 1 ? 'increased' : 'reduced') + ' to match. Guests already booked are not changed.');
    }
  };

  learn(w => w.rain >= WET_MM, 'wet', 'Rain is forecast (' + weather.rain.toFixed(1) + ' mm).');
  learn(w => w.tmax >= WARM_C, 'warm', 'It is forecast to be warm (' + Math.round(weather.tmax) + '°C).');

  if (!any)
    return null;

  if (reasons.length == 0)
    reasons.push('Weather: ' + Math.round(weather.tmax) + '°C and ' + (weather.rain < 0.5 ? 'dry' : weather.rain.toFixed(1) + ' mm of rain') + '. Nothing unusual, so no change.');

  return { factor, reasons };
}

/*
  How holiday days compare with ordinary days, each holiday day measured against ordinary days on
  its own weekday so a run of holiday Saturdays does not count as a holiday effect. Needs a few
  holiday days to say anything.
*/
function holidayFactor(history: BookingHistory, learnable: number[]): number | null {
  const ratios: number[] = [];

  learnable.filter(d => history.isHoliday(d)).forEach(d => {
    const ordinary = learnable.filter(o => weekday(o) == weekday(d) && !history.isHoliday(o)).slice(0, SIMILAR_DAYS);
    if (ordinary.length >= MIN_DAYS)
      ratios.push(history.final(d) / mean(ordinary.map(o => history.final(o))));
  });

  return ratios.length >= MIN_DAYS ? median(ratios) : null;
}


/*
  Quiet / Normal / Busy / Very Busy, against how the venue's own recent days have gone - so a small
  venue's busy day is busy for it. Needs a couple of weeks of open days.
*/
export interface Bands {
  normalFrom: number;
  busyFrom: number;
  veryBusyFrom: number;
}

export function bandsAt(history: BookingHistory, snapshot: number): Bands | null {
  const finals = history.learnableDays(snapshot).slice(0, 90).map(d => history.final(d)).sort((a, b) => a - b);
  if (finals.length < 14)
    return null;

  const at = (p: number) => finals[Math.min(finals.length - 1, Math.floor(p * finals.length))];
  return { normalFrom: at(0.25), busyFrom: at(0.6), veryBusyFrom: at(0.9) };
}

export function band(bands: Bands | null, guests: number | null): string | null {
  if (bands == null || guests == null)
    return null;
  if (guests >= bands.veryBusyFrom)
    return 'Very Busy';
  if (guests >= bands.busyFrom)
    return 'Busy';
  if (guests >= bands.normalFrom)
    return 'Normal';
  return 'Quiet';
}

/*
  What the forecast would have said for each of the past days, the given number of days before each
  one, against how it actually finished. Only days it could have forecast are included.
*/
export interface BacktestRow {
  day: number;
  forecast: number;
  actual: number;
  forecastBand: string | null;
  actualBand: string | null;
}

export function backtest(history: BookingHistory, fromDay: number, toDay: number, lead: number): BacktestRow[] {
  const rows: BacktestRow[] = [];

  for (let day = fromDay; day <= toDay; day++) {
    const actual = history.final(day);
    if (actual == 0)
      continue;

    const f = forecastDay(history, day, day - lead);
    if (f.estimate == null)
      continue;

    const bands = bandsAt(history, day - lead);
    rows.push({ day, forecast: f.estimate, actual, forecastBand: band(bands, f.estimate), actualBand: band(bands, actual) });
  }

  return rows;
}
